import type { TFunction } from '@mukil/i18n';
import { FieldRow, NumericFieldRow } from '../../components/common/field_row';
import { format_date } from '../../lib/format';
import type { CompensationPayBasis } from '../../data/use_employee_compensation';
import { format_pay_amount } from './pay_amount_display';

export interface EmployeeCompensationSummaryProps {
  pay_basis: CompensationPayBasis;
  /** Minor units: the monthly salary (salaried) or the hourly rate (hourly). */
  amount_cents: number;
  /** ISO 4217 code the amount is denominated in (the employee's pay currency, else the base). */
  currency: string;
  /** Date the current pay took effect, or `null` when the employee has no history yet. */
  effective_since: string | null;
  locale: string;
  t: TFunction;
}

/**
 * Read-only pay summary shown in the EDIT employee form in place of the
 * pay-basis select and salary / rate inputs. Pay is effective-dated: an
 * in-place overwrite would silently rewrite history, so every change goes
 * through the Change salary dialog (`change_salary_dialog.tsx`) instead.
 */
export function EmployeeCompensationSummary({
  pay_basis,
  amount_cents,
  currency,
  effective_since,
  locale,
  t,
}: EmployeeCompensationSummaryProps): React.JSX.Element {
  const is_hourly = pay_basis === 'hourly';
  return (
    <div
      className="sm:col-span-2 rounded-lg border border-[color:var(--line)] px-3 py-3"
      data-testid="employee-compensation-summary"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="nv-label mb-0">
          {t('employees.compensation_current')}
        </span>
      </div>
      <FieldRow label={t('employees.form_pay_basis')}>
        {is_hourly
          ? t('employees.form_pay_basis_hourly')
          : t('employees.form_pay_basis_salaried')}
      </FieldRow>
      <NumericFieldRow
        label={
          is_hourly
            ? t('employees.form_hourly_rate')
            : t('employees.basic_salary')
        }
      >
        {format_pay_amount(amount_cents, currency, locale)}
      </NumericFieldRow>
      {effective_since !== null && (
        <p className="nv-help">
          {t('employees.compensation_in_effect_since', {
            date: format_date(effective_since, locale),
          })}
        </p>
      )}
      <p className="nv-help">{t('employees.compensation_edit_hint')}</p>
    </div>
  );
}
